import { Typography } from "@mui/material";
import {
  ApiErrorHandler,
  LOOKUP_MASTERS,
  mapLookupValueToBookingStatusFilter,
  mapLookupValueToBookingTypeSlug,
  type UpdateBookingData,
} from "api-client";
import {
  AssignCarrierDrawer,
  type AssignCarrierSubmitPayload,
  BookingDetailDrawer,
  BookingsTable,
  type BookingsTableSortField,
  BookingsToolbar,
  type BookingsToolbarFilterOption,
  ListFiltersDrawer,
  ListAppliedFilterChips,
  ListTablePaginationBar,
  type ListAppliedFilterChipItem,
  type InviteCarrierLookupResult,
  useNotificationSnackbar,
  type BookingRow,
  useBookingCarriers,
  CommonDropdownGenerators,
  lookupItemsToFilterOptions,
  useLocationsListWithService,
  useLookup,
  buildDateRangeFilterChip,
  buildMultiSelectFilterChips,
  mergeAppliedFilterChips,
  resolveAssignCarrierToolbarAction,
  resolveAssignCarrierActionLabel,
  applyDetailQuantitiesToBookingRow,
  resolveBookingDetailCarrierHandlers,
  formatBookingCarrierSelectLabel,
  defaultBookingsListDateRange,
  defaultBookingsListDateRangeLabel,
  dateRangeQuery,
  isDefaultBookingsListDateRange,
  mapBookingDetailViewToDrawerModel,
  useBookingDetailDrawer,
  formatBookingCarrierStatusLabel,
  isBookingCarrierBlacklisted,
} from "design-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lookupService } from "../../../shared/services/lookup.service";
import { CreateBookingWizard } from "../components/create-booking-wizard.component";
import { EditBookingWizard } from "../components/edit-booking-wizard.component";
import { BookingDeleteDrawer } from "../components/booking-delete-drawer.component";
import {
  BOOKING_DELETE_BLOCKED_STATUSES,
  BOOKING_EDIT_BLOCKED_STATUSES,
  buildBookingDeleteSuccessDescription,
  isBookingDeleteAllowed,
  isBookingEditAllowed,
  mapBookingDeletePreviewToDrawer,
} from "../components/booking-delete.constants";
import type {
  BookingDeleteCarrierAssignment,
  BookingDeleteConfirmPayload,
  BookingDeleteSummary,
} from "../components/booking-delete-drawer.types";
import { bookingsService } from "../../../shared/services/bookings.service";
import { locationsService } from "../../../shared/services/locations.service";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import { useBookingWizardCustomers } from "../../../shared/hooks/useBookingWizardCustomers";
import {
  bookingListCustomerFilterOptions,
  bookingListLocationFilterOptions,
  pruneBookingListLocationFilterIds,
} from "../utils/booking-wizard-options";
import { executeAssignCarrierSubmit } from "../utils/execute-assign-carrier-submit.utils";
import { executeUnassignCarrier } from "../utils/execute-unassign-carrier.utils";
import {
  BOOKINGS_LIST_PAGE_SIZE,
  deleteBooking,
  fetchBookingDeletePreview,
  fetchBookings,
  markBookingDeleted,
  setBookingsCarrierFilters,
  setBookingsCustomerFilters,
  setBookingsDateRange,
  setBookingsListRange,
  setBookingsLocationFilters,
  setBookingsSearch,
  setBookingsSort,
  setBookingsStatusFilters,
  setBookingsBookingTypeFilters,
} from "../../../shared/store/bookings";
import {
  selectBookingsCarrierFilterIds,
  selectBookingsCustomerFilterIds,
  selectBookingsError,
  selectBookingsFromIndex,
  selectBookingsLoading,
  selectBookingsLocationFilterIds,
  selectBookingsRows,
  selectBookingsSearch,
  selectBookingsSortField,
  selectBookingsSortOrder,
  selectBookingsStatusFilterValues,
  selectBookingsBookingTypeFilterValues,
  selectBookingsTotal,
} from "../../../shared/store/bookings/bookings.selector";

export const BookingsListPage = () => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectBookingsRows);
  const loading = useAppSelector(selectBookingsLoading);
  const error = useAppSelector(selectBookingsError);
  const search = useAppSelector(selectBookingsSearch);
  const sortingField = useAppSelector(selectBookingsSortField);
  const sortingOrder = useAppSelector(selectBookingsSortOrder);
  const customerFilterIds = useAppSelector(selectBookingsCustomerFilterIds);
  const carrierFilterIds = useAppSelector(selectBookingsCarrierFilterIds);
  const locationFilterIds = useAppSelector(selectBookingsLocationFilterIds);
  const appliedStatusFilterValues = useAppSelector(selectBookingsStatusFilterValues);
  const appliedBookingTypeFilterValues = useAppSelector(selectBookingsBookingTypeFilterValues);
  const totalBookings = useAppSelector(selectBookingsTotal);
  const fromIndex = useAppSelector(selectBookingsFromIndex);

  const listPage = Math.floor(fromIndex / BOOKINGS_LIST_PAGE_SIZE) + 1;
  const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);

  const resetListWindowFirstPage = useCallback(() => {
    dispatch(
      setBookingsListRange({
        fromIndex: 0,
        toIndex: BOOKINGS_LIST_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  // Clear selection when data is refetched
  const handleClearSelection = useCallback(() => {
    setSelectedBookingIds([]);
  }, []);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const from = (nextPage - 1) * BOOKINGS_LIST_PAGE_SIZE;
      dispatch(
        setBookingsListRange({
          fromIndex: from,
          toIndex: from + BOOKINGS_LIST_PAGE_SIZE,
        }),
      );
      dispatch(fetchBookings());
    },
    [dispatch],
  );

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [editBookingOpen, setEditBookingOpen] = useState(false);
  const [editBookingId, setEditBookingId] = useState<number | null>(null);
  const [filterDraft, setFilterDraft] = useState<Record<string, string[]>>({});
  const defaultDateRangeRef = useRef(defaultBookingsListDateRange());
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => defaultDateRangeRef.current);
  const activeDeletePreviewBookingIdRef = useRef<number | null>(null);
  const OPERATOR_ID = 1;
  const {
    customers: wizardCustomers,
    loading: wizardCustomersLoading,
  } = useBookingWizardCustomers({ enabled: true, operatorId: OPERATOR_ID });
  const {
    locations: operatorLocationsResponse,
    loading: operatorLocationsLoading,
  } = useLocationsListWithService(locationsService, {}, { immediate: true });
  const operatorLocationRows = operatorLocationsResponse?.data?.locations ?? null;
  const {
    carriers: carriersFromApi,
    loading: carriersLoading,
  } = useBookingCarriers(bookingsService);
  const [assignCarrierBooking, setAssignCarrierBooking] = useState<BookingRow | null>(null);
  const {
    carriers: assignDrawerCarriersFromApi,
    loading: assignDrawerCarriersLoading,
  } = useBookingCarriers(bookingsService, {
    customerId: assignCarrierBooking?.customerId ?? null,
    enabled: assignCarrierBooking != null,
  });
  const [bookingToDelete, setBookingToDelete] = useState<BookingDeleteSummary | null>(null);
  const [deleteCarrierAssignments, setDeleteCarrierAssignments] = useState<BookingDeleteCarrierAssignment[]>([]);
  const [deleteContainerQuantity, setDeleteContainerQuantity] = useState<number | null>(null);
  const [deleteIsMultipiece, setDeleteIsMultipiece] = useState(false);
  const [deleteCanNotifyCarrier, setDeleteCanNotifyCarrier] = useState(false);
  const [deleteInitialRecipients, setDeleteInitialRecipients] = useState<string[]>([]);
  const [deleteInitialMessageBody, setDeleteInitialMessageBody] = useState("");
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleteSubmitError, setDeleteSubmitError] = useState<string | null>(null);
  const { showNotification, snackbar } = useNotificationSnackbar();

  const fetchDetailView = useCallback(async (bookingId: number) => {
    const res = await bookingsService.getBookingDetailView(bookingId);
    return mapBookingDetailViewToDrawerModel(res.data);
  }, []);

  const handleInvalidBookingId = useCallback(
    (message: string) => {
      showNotification({
        variant: "error",
        title: "Cannot open details",
        description: message,
      });
    },
    [showNotification],
  );

  const {
    detailOpen,
    detailLoading,
    detailError,
    detailModel,
    detailContextRow,
    detailBookingId,
    handleBookingNumberClick,
    openBookingDetailById,
    handleCloseDetail,
    refreshDetail,
  } = useBookingDetailDrawer({
    fetchDetailView,
    onInvalidBookingId: handleInvalidBookingId,
  });

  useEffect(() => {
    defaultDateRangeRef.current = defaultBookingsListDateRange();
    const [dateFrom, dateTo] = dateRangeQuery(defaultDateRangeRef.current);
    setDateRange(defaultDateRangeRef.current);
    dispatch(
      setBookingsListRange({
        fromIndex: 0,
        toIndex: BOOKINGS_LIST_PAGE_SIZE,
      }),
    );
    dispatch(
      setBookingsDateRange({
        dateFrom,
        dateTo,
      }),
    );
    dispatch(fetchBookings());
  }, [dispatch]);

  const handleSearchTrigger = useCallback(
    (searchValue: string) => {
      dispatch(setBookingsSearch(searchValue));
      resetListWindowFirstPage();
      handleClearSelection();
      dispatch(fetchBookings());
    },
    [dispatch, resetListWindowFirstPage, handleClearSelection],
  );

  const resetDateRangeToDefault = useCallback(() => {
    defaultDateRangeRef.current = defaultBookingsListDateRange();
    const [dateFrom, dateTo] = dateRangeQuery(defaultDateRangeRef.current);
    dispatch(setBookingsDateRange({ dateFrom, dateTo }));
    setDateRange(defaultDateRangeRef.current);
  }, [dispatch]);

  const customerOptions: BookingsToolbarFilterOption[] = useMemo(
    () => bookingListCustomerFilterOptions(wizardCustomers),
    [wizardCustomers],
  );

  const locationOptions: BookingsToolbarFilterOption[] = useMemo(
    () => bookingListLocationFilterOptions(wizardCustomers, customerFilterIds, operatorLocationRows),
    [wizardCustomers, customerFilterIds, operatorLocationRows],
  );

  const locationsLoading = useMemo(
    () =>
      customerFilterIds.length > 0 ? wizardCustomersLoading : operatorLocationsLoading,
    [customerFilterIds.length, wizardCustomersLoading, operatorLocationsLoading],
  );

  const carrierOptions: BookingsToolbarFilterOption[] = useMemo(
    () => CommonDropdownGenerators.carriers(carriersFromApi?.carriers ?? null),
    [carriersFromApi]
  );

  const { items: bookingStatusLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.bookingStatus);
  const { items: bookingTypeLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.bookingType);
  const bookingStatusFilterOptions = useMemo(
    () => lookupItemsToFilterOptions(bookingStatusLookupItems, mapLookupValueToBookingStatusFilter),
    [bookingStatusLookupItems],
  );
  const bookingTypeFilterOptions = useMemo(
    () =>
      lookupItemsToFilterOptions(bookingTypeLookupItems, (value) => {
        const slug = mapLookupValueToBookingTypeSlug(value);
        return slug ?? value.trim().toLowerCase();
      }),
    [bookingTypeLookupItems],
  );

  const listFilterSections = useMemo(
    () => [
      { id: "status", label: "Status", options: bookingStatusFilterOptions },
      { id: "bookingType", label: "Booking Type", options: bookingTypeFilterOptions },
    ],
    [bookingStatusFilterOptions, bookingTypeFilterOptions],
  );

  const appliedChipItems: ListAppliedFilterChipItem[] = useMemo(() => {
    const statusChips: ListAppliedFilterChipItem[] = [];
    for (const v of appliedStatusFilterValues) {
      const option = bookingStatusFilterOptions.find((opt) => opt.value === v);
      statusChips.push({
        id: `status:${v}`,
        label: option?.label || v,
        onRemove: () => {
          dispatch(setBookingsStatusFilters(appliedStatusFilterValues.filter((val) => val !== v)));
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      });
    }

    const bookingTypeChips: ListAppliedFilterChipItem[] = [];
    for (const v of appliedBookingTypeFilterValues) {
      const option = bookingTypeFilterOptions.find((opt) => opt.value === v);
      bookingTypeChips.push({
        id: `bookingType:${v}`,
        label: option?.label || v,
        onRemove: () => {
          dispatch(
            setBookingsBookingTypeFilters(appliedBookingTypeFilterValues.filter((val) => val !== v)),
          );
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      });
    }

    return mergeAppliedFilterChips(
      statusChips,
      bookingTypeChips,
      buildMultiSelectFilterChips({
        sectionId: "customer",
        selectedIds: customerFilterIds,
        options: customerOptions,
        onRemoveId: (id) => {
          const nextCustomerIds = customerFilterIds.filter((v) => v !== id);
          dispatch(setBookingsCustomerFilters(nextCustomerIds));
          const prunedLocationIds = pruneBookingListLocationFilterIds(
            wizardCustomers,
            nextCustomerIds,
            locationFilterIds,
            operatorLocationRows,
          );
          if (prunedLocationIds.length !== locationFilterIds.length) {
            dispatch(setBookingsLocationFilters(prunedLocationIds));
          }
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      }),
      buildMultiSelectFilterChips({
        sectionId: "location",
        selectedIds: locationFilterIds,
        options: locationOptions,
        onRemoveId: (id) => {
          dispatch(setBookingsLocationFilters(locationFilterIds.filter((v: string) => v !== id)));
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      }),
      buildMultiSelectFilterChips({
        sectionId: "carrier",
        selectedIds: carrierFilterIds,
        options: carrierOptions,
        onRemoveId: (id) => {
          dispatch(setBookingsCarrierFilters(carrierFilterIds.filter((v) => v !== id)));
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      }),
      buildDateRangeFilterChip({
        dateRange: isDefaultBookingsListDateRange(dateRange, defaultDateRangeRef.current)
          ? [null, null]
          : dateRange,
        onRemove: () => {
          resetDateRangeToDefault();
          resetListWindowFirstPage();
          handleClearSelection();
          dispatch(fetchBookings());
        },
      }),
    );
  }, [
    appliedStatusFilterValues,
    appliedBookingTypeFilterValues,
    bookingStatusFilterOptions,
    bookingTypeFilterOptions,
    customerFilterIds,
    customerOptions,
    locationFilterIds,
    locationOptions,
    carrierFilterIds,
    carrierOptions,
    dateRange,
    wizardCustomers,
    operatorLocationRows,
    dispatch,
    resetDateRangeToDefault,
    resetListWindowFirstPage,
    handleClearSelection,
  ]);

  const handleClearAllChips = useCallback(() => {
    dispatch(setBookingsStatusFilters([]));
    dispatch(setBookingsBookingTypeFilters([]));
    dispatch(setBookingsCustomerFilters([]));
    dispatch(setBookingsLocationFilters([]));
    dispatch(setBookingsCarrierFilters([]));
    resetDateRangeToDefault();
    resetListWindowFirstPage();
    handleClearSelection();
    dispatch(fetchBookings());
  }, [dispatch, resetDateRangeToDefault, resetListWindowFirstPage, handleClearSelection]);

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({
      status: [...appliedStatusFilterValues],
      bookingType: [...appliedBookingTypeFilterValues],
    });
    setFilterDrawerOpen(true);
  }, [appliedStatusFilterValues, appliedBookingTypeFilterValues]);

  const handleFilterDrawerClose = useCallback(() => setFilterDrawerOpen(false), []);

  const handleFilterApply = useCallback(
    (applied: Record<string, string[]>) => {
      dispatch(setBookingsStatusFilters(applied.status ?? []));
      dispatch(setBookingsBookingTypeFilters(applied.bookingType ?? []));
      resetListWindowFirstPage();
      handleClearSelection();
      dispatch(fetchBookings());
      setFilterDrawerOpen(false);
    },
    [dispatch, resetListWindowFirstPage, handleClearSelection],
  );

  const handleDateRangeChange = useCallback(
    (next: [Date | null, Date | null]) => {
      setDateRange(next);
      const [from, to] = dateRangeQuery(next);
      if (from != null && to != null) {
        dispatch(setBookingsDateRange({ dateFrom: from, dateTo: to }));
        resetListWindowFirstPage();
        handleClearSelection();
        dispatch(fetchBookings());
      } else if (from == null && to == null) {
        dispatch(setBookingsDateRange({ dateFrom: null, dateTo: null }));
        resetListWindowFirstPage();
        handleClearSelection();
        dispatch(fetchBookings());
      }
    },
    [dispatch, resetListWindowFirstPage, handleClearSelection],
  );

  const handleCustomersChange = useCallback(
    (ids: string[]) => {
      dispatch(setBookingsCustomerFilters(ids));
      const prunedLocationIds = pruneBookingListLocationFilterIds(
        wizardCustomers,
        ids,
        locationFilterIds,
        operatorLocationRows,
      );
      if (prunedLocationIds.length !== locationFilterIds.length) {
        dispatch(setBookingsLocationFilters(prunedLocationIds));
      }
      resetListWindowFirstPage();
      handleClearSelection();
      dispatch(fetchBookings());
    },
    [
      dispatch,
      wizardCustomers,
      operatorLocationRows,
      locationFilterIds,
      resetListWindowFirstPage,
      handleClearSelection,
    ],
  );

  const handleLocationsChange = useCallback(
    (ids: string[]) => {
      dispatch(setBookingsLocationFilters(ids));
      resetListWindowFirstPage();
      handleClearSelection();
      dispatch(fetchBookings());
    },
    [dispatch, resetListWindowFirstPage, handleClearSelection],
  );

  const handleCarriersChange = useCallback(
    (ids: string[]) => {
      dispatch(setBookingsCarrierFilters(ids));
      resetListWindowFirstPage();
      handleClearSelection();
      dispatch(fetchBookings());
    },
    [dispatch, resetListWindowFirstPage, handleClearSelection],
  );

  const handleSortHeaderClick = useCallback((field: BookingsTableSortField) => {
    resetListWindowFirstPage();
    const nextOrder = sortingField === field && sortingOrder === "asc" ? "desc" : "asc";
    dispatch(
      setBookingsSort({
        sortingOrder: nextOrder,
        sortingField: field,
      }),
    );
    handleClearSelection();
    dispatch(fetchBookings());
  }, [dispatch, resetListWindowFirstPage, sortingField, sortingOrder, handleClearSelection]);

  const assignCarrierToolbarAction = useMemo(
    () => resolveAssignCarrierToolbarAction(selectedBookingIds, rows),
    [rows, selectedBookingIds],
  );

  const detailAssignToolbarAction = useMemo(() => {
    if (!detailContextRow) {
      return null;
    }
    return resolveAssignCarrierToolbarAction([detailContextRow.id], rows);
  }, [detailContextRow, rows]);

  // Enable actions based on selection
  const assignCarrierEnabled = assignCarrierToolbarAction.enabled;
  const assignCarrierLabel = assignCarrierToolbarAction.label;
  const selectedBookingRow = useMemo(() => {
    if (selectedBookingIds.length !== 1) return null;
    return rows.find((r) => r.id === selectedBookingIds[0]) ?? null;
  }, [rows, selectedBookingIds]);

  const editEnabled = selectedBookingRow != null && isBookingEditAllowed(selectedBookingRow.status);
  const deleteEnabled =
    selectedBookingIds.length === 1 &&
    selectedBookingRow != null &&
    isBookingDeleteAllowed(selectedBookingRow.status);

  const handleBulkAssignCarrier = useCallback(() => {
    const { enabled, selectedRow } = resolveAssignCarrierToolbarAction(
      selectedBookingIds,
      rows,
    );
    if (!enabled || !selectedRow) return;

    setAssignCarrierBooking(selectedRow);
  }, [rows, selectedBookingIds]);

  const handleBulkEdit = useCallback(() => {
    if (selectedBookingIds.length !== 1) return;
    const row = rows.find((r) => r.id === selectedBookingIds[0]);
    if (!row) return;
    if (BOOKING_EDIT_BLOCKED_STATUSES.includes(row.status)) {
      showNotification({
        variant: "warning",
        title: "Cannot edit",
        description: `Bookings with status "${row.status}" cannot be edited.`,
      });
      return;
    }
    if (!isBookingEditAllowed(row.status)) return;

    const bookingIdNum = parseInt(row.id, 10);
    if (!Number.isFinite(bookingIdNum)) {
      showNotification({
        variant: "error",
        title: "Cannot edit",
        description: "Invalid booking id.",
      });
      return;
    }

    setEditBookingId(bookingIdNum);
    setEditBookingOpen(true);
  }, [rows, selectedBookingIds, showNotification]);

  const handleCloseEditBooking = useCallback(() => {
    setEditBookingOpen(false);
    setEditBookingId(null);
  }, []);

  const handleEditBookingUpdated = useCallback(
    (data: UpdateBookingData, _message: string) => {
      const bookingNumber = data.bookingNumber?.trim();
      showNotification({
        variant: "success",
        title: "Booking updated",
        description: bookingNumber
          ? `Booking number - ${bookingNumber} has been updated successfully`
          : "Booking has been updated successfully",
      });
      dispatch(fetchBookings());
    },
    [dispatch, showNotification],
  );

  // Booking-delete (Phase 1 — D-014, D-007 / D-022).
  // Replaces the previous placeholder `handleBulkDelete` snackbar stub.
  const resetDeleteDrawerState = useCallback(() => {
    activeDeletePreviewBookingIdRef.current = null;
    setBookingToDelete(null);
    setDeleteCarrierAssignments([]);
    setDeleteContainerQuantity(null);
    setDeleteIsMultipiece(false);
    setDeleteCanNotifyCarrier(false);
    setDeleteInitialRecipients([]);
    setDeleteInitialMessageBody("");
    setDeletePreviewLoading(false);
    setDeleteSubmitError(null);
  }, []);

  const openDeletePreviewForRow = useCallback(
    (row: BookingRow) => {
      if (BOOKING_DELETE_BLOCKED_STATUSES.includes(row.status)) {
        showNotification({
          variant: "warning",
          title: "Cannot delete",
          description: `Bookings with status "${row.status}" cannot be deleted.`,
        });
        return;
      }
      const bookingIdNum = parseInt(row.id, 10);
      if (!Number.isFinite(bookingIdNum)) {
        showNotification({
          variant: "error",
          title: "Cannot delete",
          description: "Invalid booking id.",
        });
        return;
      }

      activeDeletePreviewBookingIdRef.current = bookingIdNum;
      setDeletePreviewLoading(true);
      setBookingToDelete({
        bookingId: row.id,
        bookingNumber: row.bookingNumber,
        arrivalAt: row.arrivalAt,
        locationLabel: row.locationLabel,
      });
      setDeleteCarrierAssignments([]);
      setDeleteContainerQuantity(null);
      setDeleteIsMultipiece(false);
      setDeleteCanNotifyCarrier(false);
      setDeleteInitialRecipients([]);
      setDeleteInitialMessageBody("");

      void (async () => {
        try {
          const res = await dispatch(fetchBookingDeletePreview(bookingIdNum)).unwrap();
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          const mapped = mapBookingDeletePreviewToDrawer(res.data);
          setBookingToDelete(mapped.booking);
          setDeleteCarrierAssignments(mapped.carrierAssignments);
          setDeleteContainerQuantity(mapped.containerQuantity);
          setDeleteIsMultipiece(mapped.isMultipiece);
          setDeleteCanNotifyCarrier(mapped.canNotifyCarrier);
          setDeleteInitialRecipients(mapped.initialRecipients);
          setDeleteInitialMessageBody(mapped.initialMessageBody);
        } catch (e: unknown) {
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          resetDeleteDrawerState();
          const message =
            typeof e === "string" ? e : ApiErrorHandler.extractAxiosError(e, "Failed to load delete preview.");
          showNotification({
            variant: "error",
            title: "Cannot delete",
            description: message,
          });
        } finally {
          if (activeDeletePreviewBookingIdRef.current === bookingIdNum) {
            setDeletePreviewLoading(false);
          }
        }
      })();
    },
    [dispatch, resetDeleteDrawerState, showNotification],
  );

  const handleDeleteBooking = useCallback(() => {
    if (selectedBookingIds.length !== 1) return;
    const id = selectedBookingIds[0];
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    openDeletePreviewForRow(row);
  }, [openDeletePreviewForRow, rows, selectedBookingIds]);

  const handleCloseDeleteDrawer = useCallback(() => {
    if (deletePreviewLoading) return;
    resetDeleteDrawerState();
  }, [deletePreviewLoading, resetDeleteDrawerState]);

  const handleClearDeleteSubmitError = useCallback(() => {
    setDeleteSubmitError(null);
  }, []);

  const handleDetailEdit = useCallback(() => {
    if (detailBookingId == null) return;
    handleCloseDetail();
    setEditBookingId(detailBookingId);
    setEditBookingOpen(true);
  }, [detailBookingId, handleCloseDetail]);

  const handleDetailReassign = useCallback(() => {
    if (!detailContextRow) return;
    if (!detailModel?.isMultipieceBooking) {
      handleCloseDetail();
    }
    const listRow = rows.find((r) => r.id === detailContextRow.id) ?? detailContextRow;
    setAssignCarrierBooking(applyDetailQuantitiesToBookingRow(listRow, detailModel));
  }, [detailContextRow, detailModel, handleCloseDetail, rows]);

  const handleDetailUnassignCarrier = useCallback(
    async (assignmentId: number) => {
      const assignment = detailModel?.carrierAssignments.find(
        (row) => row.assignmentId === assignmentId,
      );
      await executeUnassignCarrier({
        bookingId: detailBookingId,
        assignmentId,
        carrierName: assignment?.carrierName ?? "Carrier",
        bookingNumber: detailModel?.bookingNumber ?? "",
        showNotification,
        refreshDetail,
        onListRefresh: () => {
          dispatch(fetchBookings());
        },
      });
    },
    [detailBookingId, detailModel, dispatch, refreshDetail, showNotification],
  );

  const handleDetailDelete = useCallback(() => {
    if (!detailContextRow) return;
    const row = detailContextRow;
    handleCloseDetail();
    openDeletePreviewForRow(row);
  }, [detailContextRow, handleCloseDetail, openDeletePreviewForRow]);

  const handleConfirmDeleteBooking = useCallback(
    async (payload: BookingDeleteConfirmPayload) => {
      const bookingIdNum = Number(payload.bookingId);
      if (!Number.isFinite(bookingIdNum)) {
        setDeleteSubmitError("Invalid booking id.");
        showNotification({
          variant: "error",
          title: "Cannot delete",
          description: "Invalid booking id.",
        });
        return;
      }
      setDeleteSubmitError(null);
      try {
        const res = await dispatch(
          deleteBooking({
            bookingId: bookingIdNum,
            recipientEmails: payload.recipients.join(","),
            emailMessage: payload.messageBody,
            ...(payload.reason !== undefined ? { reason: payload.reason } : {}),
          }),
        ).unwrap();
        const bookingNumber =
          res.data.bookingNumber?.trim() || bookingToDelete?.bookingNumber?.trim() || "";
        dispatch(markBookingDeleted(payload.bookingId));
        setSelectedBookingIds([]);
        resetDeleteDrawerState();
        const apiMessage = res.message?.trim();
        const description = buildBookingDeleteSuccessDescription(
          bookingNumber,
          apiMessage,
          res.data.emailSent,
        );
        showNotification({
          variant: "success",
          title: "Booking deleted",
          description,
        });
      } catch (e: unknown) {
        const message =
          typeof e === "string" ? e : ApiErrorHandler.extractAxiosError(e, "Failed to delete booking.");
        setDeleteSubmitError(message);
        showNotification({
          variant: "error",
          title: "Delete failed",
          description: message,
        });
      }
    },
    [bookingToDelete, dispatch, resetDeleteDrawerState, showNotification],
  );

  const carrierAssignSelectOptions = useMemo(
    () =>
      (assignDrawerCarriersFromApi?.carriers ?? []).map((c) => {
        const blacklisted = isBookingCarrierBlacklisted(c.status);
        return {
          value: String(c.carrierId),
          label: formatBookingCarrierSelectLabel(c.carrierName, c.dotNumber),
          plainLabel: c.carrierName,
          disabled: blacklisted,
          statusChipLabel: blacklisted ? formatBookingCarrierStatusLabel(c.status) : undefined,
        };
      }),
    [assignDrawerCarriersFromApi],
  );

  const handleOpenAssignCarrier = useCallback((row: BookingRow) => {
    setAssignCarrierBooking(row);
  }, []);

  const handleCloseAssignCarrier = useCallback(() => {
    setAssignCarrierBooking(null);
  }, []);

  const handleDotLookup = useCallback(
    async (dot: string): Promise<InviteCarrierLookupResult> => {
      const res = await bookingsService.lookupCarrierByDot(dot);
      if (res.statusCode !== 200) {
        throw new Error(res.message?.trim() || "Could not look up carrier by DOT. Try again.");
      }
      const d = res.data;
      return {
        carrierName: d.carrierName,
        contactName: d.contactName,
        email: d.email,
        businessAddress: d.businessAddress,
        dotStatus: d.dotStatus,
        riskScore: d.riskScore,
        safetyScore: String(d.safetyScore),
      };
    },
    [],
  );

  const handleConfirmAssignCarrier = useCallback(
    async (payload: AssignCarrierSubmitPayload) => {
      const row = rows.find((r) => r.id === payload.bookingId) ?? assignCarrierBooking;
      if (!row) {
        throw new Error("Booking not found. Refresh the list and try again.");
      }
      await executeAssignCarrierSubmit({
        row,
        payload,
        showNotification,
        onSuccess: () => {
          setAssignCarrierBooking(null);
          handleClearSelection();
          dispatch(fetchBookings());
          if (detailOpen && detailBookingId != null) {
            void refreshDetail();
          }
        },
      });
    },
    [
      assignCarrierBooking,
      detailBookingId,
      detailOpen,
      dispatch,
      handleClearSelection,
      refreshDetail,
      rows,
      showNotification,
    ],
  );

  return (
    <div className="bookings-page">
      <Typography variant="h5" className="bookings-page__title" component="h1">
        Bookings
      </Typography>
      <div className="users-page__main">
        <BookingsToolbar
          variant="operator"
          searchValue={search}
          onSearchTrigger={handleSearchTrigger}
          onFilterClick={openFilterDrawer}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          dateRangeLabel={defaultBookingsListDateRangeLabel(dateRange, defaultDateRangeRef.current)}
          customerOptions={customerOptions}
          selectedCustomerIds={customerFilterIds}
          onCustomersChange={handleCustomersChange}
          customersLoading={wizardCustomersLoading}
          locationOptions={locationOptions}
          selectedLocationIds={locationFilterIds}
          onLocationsChange={handleLocationsChange}
          locationsLoading={locationsLoading}
          carrierOptions={carrierOptions}
          selectedCarrierIds={carrierFilterIds}
          onCarriersChange={handleCarriersChange}
          carriersLoading={carriersLoading}
          onCreateBookingClick={() => setCreateBookingOpen(true)}
          onAssignCarrierClick={handleBulkAssignCarrier}
          onEditClick={handleBulkEdit}
          onDeleteClick={handleDeleteBooking}
          assignCarrierEnabled={assignCarrierEnabled}
          assignCarrierLabel={assignCarrierLabel}
          editEnabled={editEnabled}
          deleteEnabled={deleteEnabled}
        />
        <ListAppliedFilterChips 
          items={appliedChipItems} 
          onClearAll={handleClearAllChips}
          totalResults={totalBookings}
        />
        <div className="users-page__table-scroll">
          <BookingsTable
            variant="operator"
            loading={loading}
            rows={error ? [] : rows}
            selectedBookingIds={selectedBookingIds}
            onSelectedBookingIdsChange={setSelectedBookingIds}
            sortField={sortingField}
            sortOrder={sortingOrder}
            onSortHeaderClick={handleSortHeaderClick}
            onBookingNumberClick={handleBookingNumberClick}
            onRowAssignCarrierClick={handleOpenAssignCarrier}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={totalBookings}
                page={listPage}
                pageSize={BOOKINGS_LIST_PAGE_SIZE}
                onPageChange={handlePageChange}
                disabled={loading}
              />
            }
          />
        </div>
      </div>
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        title="Filters"
        subtitle="Select filters for bookings."
        sections={listFilterSections}
        value={filterDraft}
        onChange={setFilterDraft}
        onApply={handleFilterApply}
      />
      <CreateBookingWizard
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        operatorId={OPERATOR_ID}
        onBookingCreated={(data) => {
          showNotification({
            variant: "success",
            title: "Booking created",
            description: data.bookingNumber
              ? `Booking ${data.bookingNumber} was created successfully.`
              : "Booking was created successfully.",
            actions: [
              {
                label: "View detail",
                onClick: () => openBookingDetailById(data.bookingId),
              },
            ],
          });
          dispatch(fetchBookings());
        }}
      />
      <EditBookingWizard
        open={editBookingOpen}
        bookingId={editBookingId}
        onClose={handleCloseEditBooking}
        operatorId={OPERATOR_ID}
        onBookingUpdated={handleEditBookingUpdated}
      />
      <AssignCarrierDrawer
        open={assignCarrierBooking != null}
        onClose={handleCloseAssignCarrier}
        booking={assignCarrierBooking}
        carrierOptions={carrierAssignSelectOptions}
        carriersLoading={assignDrawerCarriersLoading}
        onAssign={handleConfirmAssignCarrier}
        onDotLookup={handleDotLookup}
      />
      <BookingDeleteDrawer
        key={bookingToDelete?.bookingId ?? "closed"}
        open={bookingToDelete != null}
        onClose={handleCloseDeleteDrawer}
        booking={bookingToDelete}
        carrierAssignments={deleteCarrierAssignments}
        containerQuantity={deleteContainerQuantity}
        isMultipiece={deleteIsMultipiece}
        canNotifyCarrier={deleteCanNotifyCarrier}
        initialRecipients={deleteInitialRecipients}
        initialMessageBody={deleteInitialMessageBody}
        loading={deletePreviewLoading}
        submitError={deleteSubmitError}
        onClearSubmitError={handleClearDeleteSubmitError}
        onConfirm={handleConfirmDeleteBooking}
      />
      <BookingDetailDrawer
        open={detailOpen}
        loading={detailLoading}
        error={detailError}
        detail={detailModel}
        customerDetailLink={
          detailContextRow?.customerId && detailContextRow.customerStatus !== "Deleted"
            ? { to: `/customers/${detailContextRow.customerId}` }
            : null
        }
        carrierDetailLink={
          detailModel?.carrierId != null ? { to: `/carriers/${detailModel.carrierId}` } : null
        }
        onClose={handleCloseDetail}
        onDone={handleCloseDetail}
        onEdit={detailModel?.actionsAllowed ? handleDetailEdit : undefined}
        {...resolveBookingDetailCarrierHandlers({
          detail: detailModel,
          singlePieceAssignEnabled: Boolean(detailAssignToolbarAction?.enabled),
          onAssignOrReassign: handleDetailReassign,
          onUnassign: handleDetailUnassignCarrier,
        })}
        assignCarrierMenuLabel={
          detailContextRow
            ? resolveAssignCarrierActionLabel(detailContextRow.status, detailContextRow)
            : null
        }
        onDelete={detailModel?.actionsAllowed ? handleDetailDelete : undefined}
      />
      {snackbar}
    </div>
  );
};
